/**
 * Opening webmail: a single-use credential from the SSO endpoint, POSTED BY THE BROWSER as a form in
 * a new tab. Not a link: the webmail accepts the credential only as a form POST, and the session
 * cookie is set on the provider's domain, so a server-side POST would sign in the server, not the
 * operator.
 *
 * Rules the callers keep: call it from the click, never on load or mount (the token dies on first
 * use); never store or reuse the answer (a second click is a second call); never log it (it signs in
 * as the mailbox); never call it for a `deleted` mailbox (disable the button; the service answers
 * `409`).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { MailboxRecord, MailboxSso } from '../../mock-data/mailbox-records';
import { mailboxService } from './api/service';
import { mailboxErrorText, takeMailboxError } from './errors';

/** Build the sign-in form in `doc` from the SSO answer, and submit it. The field names come from
 *  the answer, never from here. */
const postSsoForm = (doc: Document, sso: MailboxSso): void => {
  // The tab is about:blank and so SAME-ORIGIN with this app: a `javascript:` action would run as
  // us, and an `http:` one would post the credential in the clear. Only an https login page is
  // ever posted to; anything else rejects (and the caller closes the tab).
  let protocol = '';
  try {
    protocol = new URL(sso.loginUrl).protocol;
  } catch {
    protocol = '';
  }
  if (protocol !== 'https:') throw new Error('webmail: the login URL is not https');
  const form = doc.createElement('form');
  form.method = 'POST';
  form.action = sso.loginUrl;
  for (const [name, value] of [
    [sso.usernameField, sso.email],
    [sso.passwordField, sso.token],
  ]) {
    const input = doc.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  doc.body.appendChild(form);
  form.submit();
};

/**
 * Open `mailbox`'s webmail in a new tab. Call it SYNCHRONOUSLY from the click handler. Rejects when
 * the tab could not be opened or the SSO call failed; the tab is closed first, so the caller only
 * has to say so.
 */
export const openWebmail = async (
  mailbox: Pick<MailboxRecord, 'email' | 'status'>,
  requestSso: (mailbox: Pick<MailboxRecord, 'email' | 'status'>) => Promise<MailboxSso>,
): Promise<void> => {
  // The tab MUST be opened here, synchronously, BEFORE the await below. After an async gap the
  // click's gesture context is gone and every browser's popup blocker eats the window.open. This
  // looks like a pointless line to anyone tidying up; moving it after the await is the most likely
  // reason a first integration "does not work".
  const tab = window.open('', '_blank');
  if (!tab) throw new Error('webmail: the new tab was blocked');
  // Cut the tab's handle back to this page before it navigates to the provider's origin.
  tab.opener = null;
  try {
    const sso = await requestSso(mailbox);
    postSsoForm(tab.document, sso);
  } catch (error) {
    tab.close();
    throw error;
  }
};

/**
 * The Webmail click handler both routes use: opens webmail, and on a failure says so in the page's
 * toast. The service's own `message` is shown verbatim; on a `409` (no single sign-on for this
 * mailbox) it is followed by the fallback the service names, the password. A failure that is not
 * the service answering (a blocked tab, the network) has no message of its own, so it gets ours.
 * Pass the handler straight to the button's click: it opens the tab before it awaits anything. The
 * SSO call goes through the source switch (the mock, or ows-mail) for the section's `domain`.
 */
export const useOpenWebmail = (
  show: (message: string) => void,
  domain: string,
): ((mailbox: Pick<MailboxRecord, 'email' | 'status'>) => void) => {
  const { t } = useTranslation();
  return React.useCallback(
    (mailbox) => {
      openWebmail(mailbox, (target) => mailboxService.requestSso(domain, target)).catch((rejection: unknown) => {
        const error = takeMailboxError(rejection);
        if (error === null) show(t('Webmail could not be opened. Sign in with the mailbox password instead.'));
        else if (error.code === 'conflict') {
          show(t('{{message}}. Sign in with the mailbox password instead.', { message: error.message }));
        } else show(mailboxErrorText(error, t, t('Webmail could not be opened. Sign in with the mailbox password instead.')));
      });
    },
    [show, t, domain],
  );
};
